import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import CreatePartyModal from "./components/CreatePartyModal";
import TeamLeaderboard from "./components/TeamLeaderboard";

type TabId = "browse" | "create" | "leaderboard";

const INITIAL_CHALLENGES = [
  {
    id: 1,
    title: "AI로 숏폼 만들기 대작전",
    desc: "각자 AI 툴로 이미지, 배경음악, 대본, 영상 편집을 맡아 하나의 숏폼 영상을 완성하세요.",
    category: "영상 제작",
    deadline: "D-2",
    points: 1200,
    difficulty: "보통",
    roles: [
      { id: 1, name: "이미지 담당", icon: "🎨", desc: "Midjourney로 썸네일 & 이미지 생성", filled: true, user: { name: "한예린", avatar: "https://public.readdy.ai/ai/img_res/057bde2c1192045e09dae2300ab59f50.jpg" } },
      { id: 2, name: "대본 담당", icon: "✍️", desc: "ChatGPT로 스크립트 작성", filled: true, user: { name: "정우진", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20male%20smiling%20casual%20portrait%20clean%20background%20minimal%20headshot%20photography&width=60&height=60&seq=pr2&orientation=squarish" } },
      { id: 3, name: "배경음악 담당", icon: "🎵", desc: "Suno AI로 배경음악 생성", filled: false, user: null },
      { id: 4, name: "영상 편집 담당", icon: "🎬", desc: "AI 편집 툴로 영상 완성", filled: false, user: null },
    ],
    tags: ["Midjourney", "ChatGPT", "Suno AI"],
  },
  {
    id: 2,
    title: "AI 뉴스레터 한 편 완성하기",
    desc: "AI 최신 트렌드를 조사해서 함께 뉴스레터를 기획하고 발행해 보세요.",
    category: "콘텐츠",
    deadline: "D-5",
    points: 900,
    difficulty: "쉬움",
    roles: [
      { id: 1, name: "트렌드 리서처", icon: "🔍", desc: "Perplexity로 최신 AI 트렌드 조사", filled: true, user: { name: "김수아", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20female%20smiling%20bright%20portrait%20clean%20white%20background%20minimal%20headshot&width=60&height=60&seq=pr3&orientation=squarish" } },
      { id: 2, name: "카피라이터", icon: "📝", desc: "ChatGPT로 매력적인 글 작성", filled: false, user: null },
      { id: 3, name: "디자이너", icon: "🎨", desc: "DALL-E로 뉴스레터 이미지 생성", filled: false, user: null },
    ],
    tags: ["Perplexity", "ChatGPT", "DALL-E"],
  },
  {
    id: 3,
    title: "AI 스타트업 피치덱 만들기",
    desc: "AI로 비즈니스 아이디어를 다듬고 투자자용 피치덱을 완성해 보세요.",
    category: "비즈니스",
    deadline: "D-7",
    points: 1500,
    difficulty: "어려움",
    roles: [
      { id: 1, name: "아이디어 기획자", icon: "💡", desc: "ChatGPT로 비즈니스 모델 설계", filled: true, user: { name: "이도현", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20male%20casual%20portrait%20bright%20clean%20background%20headshot%20warm&width=60&height=60&seq=pr4&orientation=squarish" } },
      { id: 2, name: "시장조사 담당", icon: "📊", desc: "Perplexity로 시장 데이터 수집", filled: true, user: { name: "박민준", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20male%20smiling%20portrait%20bright%20clean%20background%20headshot%20warm&width=60&height=60&seq=pr5&orientation=squarish" } },
      { id: 3, name: "슬라이드 디자이너", icon: "🖼️", desc: "Gamma AI로 피치덱 슬라이드 제작", filled: false, user: null },
      { id: 4, name: "발표 코치", icon: "🎤", desc: "AI로 발표 스크립트 다듬기", filled: false, user: null },
    ],
    tags: ["ChatGPT", "Perplexity", "Gamma AI"],
  },
];

const DIFFICULTY_COLOR: Record<string, string> = { "쉬움": "#00D873", "보통": "#FFD600", "어려움": "#FF6B6B" };

type Challenge = typeof INITIAL_CHALLENGES[0];

function RoleSlot({ role, onJoin }: { role: Challenge["roles"][0]; onJoin: () => void }) {
  return (
    <div className={`rounded-2xl p-4 border-2 transition-all ${role.filled ? "border-[#00D873]/30 bg-[#00D873]/5" : "border-dashed border-[#191F28]/15 bg-[#F2F4F6]/50"}`}>
      <div className="flex items-start gap-3">
        <div className="text-2xl mt-0.5">{role.icon}</div>
        <div className="flex-1">
          <div className="font-bold text-[#191F28] text-sm">{role.name}</div>
          <div className="text-xs text-[#191F28]/50 mt-0.5">{role.desc}</div>
        </div>
        {role.filled && role.user ? (
          <img src={role.user.avatar} alt={role.user.name} className="w-8 h-8 rounded-full object-cover object-top border-2 border-[#00D873]/40" />
        ) : (
          <button onClick={onJoin} className="bg-[#191F28] text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer hover:bg-[#00D873] transition-colors whitespace-nowrap">참여</button>
        )}
      </div>
    </div>
  );
}

function PartyCard({ challenge }: { challenge: Challenge }) {
  const [joined, setJoined] = useState<number[]>([]);
  const [expanded, setExpanded] = useState(false);
  const filledCount = challenge.roles.filter((r) => r.filled).length + joined.length;
  const totalSlots = challenge.roles.length;

  return (
    <div className="bg-white rounded-3xl overflow-hidden border border-[#F2F4F6]">
      <div className="p-7">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B]">파티 챌린지</span>
              <span className="text-xs font-bold px-2 py-1 rounded-full text-white" style={{ backgroundColor: DIFFICULTY_COLOR[challenge.difficulty] }}>{challenge.difficulty}</span>
              <span className="text-xs text-[#191F28]/40">{challenge.deadline} 마감</span>
            </div>
            <h3 className="text-xl font-black text-[#191F28] leading-tight">{challenge.title}</h3>
          </div>
          <div className="text-center shrink-0">
            <div className="text-xl font-black text-[#FFD600]">+{challenge.points.toLocaleString()}</div>
            <div className="text-xs text-[#191F28]/40">Grow P</div>
          </div>
        </div>
        <p className="text-sm text-[#191F28]/60 leading-relaxed mb-5">{challenge.desc}</p>
        <div className="mb-5">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-[#191F28]/50">팀원 모집</span>
            <span className="font-bold text-[#191F28]">{filledCount}/{totalSlots}명</span>
          </div>
          <div className="h-2 bg-[#F2F4F6] rounded-full overflow-hidden">
            <div className="h-full bg-[#00D873] rounded-full transition-all duration-500" style={{ width: `${(filledCount / totalSlots) * 100}%` }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {challenge.tags.map((tag) => (
            <span key={tag} className="text-xs bg-[#F2F4F6] text-[#191F28]/60 px-3 py-1 rounded-full">{tag}</span>
          ))}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="w-full text-sm font-semibold text-[#191F28]/50 flex items-center justify-center gap-1 py-2 hover:text-[#191F28] transition-colors cursor-pointer">
          {expanded ? "역할 닫기" : "역할 배분 보기"}
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} />
        </button>
      </div>
      {expanded && (
        <div className="px-7 pb-7 space-y-3 border-t border-[#F2F4F6] pt-5">
          {challenge.roles.map((role) => (
            <RoleSlot
              key={role.id}
              role={joined.includes(role.id) ? { ...role, filled: true, user: { name: "나", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20casual%20portrait%20clean%20white%20background%20minimal%20headshot&width=60&height=60&seq=me&orientation=squarish" } } : role}
              onJoin={() => { if (!joined.includes(role.id)) setJoined((p) => [...p, role.id]); }}
            />
          ))}
        </div>
      )}
      <div className="px-7 pb-7">
        {filledCount >= totalSlots ? (
          <div className="w-full bg-[#191F28] text-white font-black py-4 rounded-2xl text-base text-center">🎉 팀 구성 완료! 챌린지 시작 준비 중</div>
        ) : (
          <button onClick={() => setExpanded(true)} className="w-full bg-[#00D873] text-white font-black py-4 rounded-2xl text-base hover:opacity-90 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap">파티 합류하기 🎮</button>
        )}
      </div>
    </div>
  );
}

function MyPartyCard({ challenge, onView }: { challenge: Challenge; onView: () => void }) {
  const myRole = challenge.roles[Math.floor(Math.random() * challenge.roles.filter((r) => r.filled).length)];
  return (
    <div className="bg-white rounded-2xl p-5 border-2 border-[#00D873]/30">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <span className="text-xs font-bold bg-[#00D873]/10 text-[#00D873] px-2.5 py-1 rounded-full">{challenge.category}</span>
          <h4 className="text-base font-black text-[#191F28] mt-2">{challenge.title}</h4>
        </div>
        <span className="text-xs font-bold text-[#FFD600] shrink-0">+{challenge.points}P</span>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg">{myRole?.icon}</span>
        <span className="text-xs text-[#8B95A1]">내 역할: <strong className="text-[#191F28]">{myRole?.name}</strong></span>
        <span className="ml-auto text-xs text-[#FF6B6B]">{challenge.deadline} 마감</span>
      </div>
      <button onClick={onView} className="w-full py-2.5 rounded-xl bg-[#F2F4F6] hover:bg-[#00D873] hover:text-white text-sm font-bold text-[#191F28] cursor-pointer transition-all whitespace-nowrap">
        팀 현황 보기 →
      </button>
    </div>
  );
}

export default function PartyPage() {
  const [activeTab, setActiveTab] = useState<TabId>("browse");
  const [challenges, setChallenges] = useState(INITIAL_CHALLENGES);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [myPartyIds] = useState([1]);
  const [successMsg, setSuccessMsg] = useState("");

  const myParties = challenges.filter((c) => myPartyIds.includes(c.id));

  const handleCreateParty = (data: Parameters<typeof CreatePartyModal>[0]["onSubmit"] extends (d: infer D) => void ? D : never) => {
    const newChallenge: Challenge = {
      id: Date.now(),
      title: data.title,
      desc: data.desc || "함께 만들어가는 AI 챌린지!",
      category: data.category,
      deadline: data.deadline,
      points: 800,
      difficulty: "보통",
      roles: data.roles.map((r, i) => ({ id: i + 1, name: r.name, icon: r.icon, desc: r.desc || "AI 툴 활용", filled: false, user: null })),
      tags: [data.category],
    };
    setChallenges((p) => [newChallenge, ...p]);
    setShowCreateModal(false);
    setSuccessMsg(`"${data.title}" 파티가 생성됐어요! 🎉`);
    setActiveTab("browse");
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const TABS = [
    { id: "browse" as TabId, label: "파티 찾기", icon: "ri-search-line" },
    { id: "create" as TabId, label: "내 파티", icon: "ri-user-heart-line", badge: myParties.length },
    { id: "leaderboard" as TabId, label: "팀 리더보드", icon: "ri-trophy-line" },
  ];

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />
      {showCreateModal && <CreatePartyModal onClose={() => setShowCreateModal(false)} onSubmit={handleCreateParty} />}

      {/* Success Toast */}
      {successMsg && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 bg-[#191F28] text-white px-6 py-3 rounded-2xl text-sm font-bold flex items-center gap-2">
          <i className="ri-check-line text-[#00D873]" />
          {successMsg}
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#191F28]/40 hover:text-[#191F28] transition-colors mb-5 cursor-pointer">
            <i className="ri-arrow-left-line" /> 대시보드로
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">🎮</span>
                <h1 className="text-3xl lg:text-4xl font-black text-[#191F28]">파티 챌린지</h1>
              </div>
              <p className="text-[#191F28]/60 text-sm">3~4명이 한 팀이 되어 AI 프로젝트를 완성해보세요.</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#191F28] text-white text-sm font-black px-5 py-3 rounded-2xl cursor-pointer hover:bg-[#00D873] transition-colors whitespace-nowrap flex items-center gap-2 shrink-0"
            >
              <i className="ri-add-line" />
              파티 만들기
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "진행 중인 파티", value: `${challenges.length}개`, icon: "🔥" },
            { label: "이번 주 완료", value: "89팀", icon: "✅" },
            { label: "최고 보상", value: "1,500P", icon: "🏆" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-4 text-center">
              <div className="text-2xl mb-1">{s.icon}</div>
              <div className="text-lg font-black text-[#191F28]">{s.value}</div>
              <div className="text-xs text-[#191F28]/40">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl p-1.5 flex gap-1 mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                activeTab === tab.id ? "bg-[#191F28] text-white" : "text-[#8B95A1] hover:text-[#191F28]"
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${activeTab === tab.id ? "bg-[#00D873] text-white" : "bg-[#F2F4F6] text-[#191F28]"}`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "browse" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-[#191F28]">모집 중인 파티</h2>
              <span className="text-sm text-[#8B95A1]">{challenges.length}개</span>
            </div>
            {challenges.map((ch) => <PartyCard key={ch.id} challenge={ch} />)}
          </div>
        )}

        {activeTab === "create" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-[#191F28]">내가 참여한 파티</h2>
              <button onClick={() => setShowCreateModal(true)} className="text-sm font-bold text-[#00D873] cursor-pointer hover:opacity-80 whitespace-nowrap flex items-center gap-1">
                <i className="ri-add-line" /> 새 파티 만들기
              </button>
            </div>
            {myParties.length === 0 ? (
              <div className="bg-white rounded-3xl py-16 text-center">
                <div className="text-4xl mb-3">🎮</div>
                <p className="text-sm text-[#8B95A1] mb-5">아직 참여한 파티가 없어요</p>
                <button onClick={() => setActiveTab("browse")} className="bg-[#00D873] text-white text-sm font-bold px-5 py-2.5 rounded-2xl cursor-pointer hover:opacity-90 whitespace-nowrap">파티 찾아보기</button>
              </div>
            ) : (
              myParties.map((ch) => <MyPartyCard key={ch.id} challenge={ch} onView={() => setActiveTab("browse")} />)
            )}
          </div>
        )}

        {activeTab === "leaderboard" && <TeamLeaderboard />}
      </div>
    </div>
  );
}
